import { forwardRef } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "../../../lib/cn";

/**
 * Phase 2 — shadcn-style Dialog on top of Radix. Replaces `.td-modal-*`,
 * `.pdf-modal-*`, `.profile-modal`, and the ad-hoc overlays in
 * task-detail.css / profile.css. Built with tokens from tailwind.config.js.
 *
 * Overlay is the full-viewport dimmed backdrop. Content centers itself
 * with a max-width, padded body. Title/Description use Radix's primitives
 * so screen readers announce modal opening.
 *
 * Usage:
 *   <Dialog open={x} onOpenChange={setX}>
 *     <DialogContent>
 *       <DialogHeader>
 *         <DialogTitle>Edit client</DialogTitle>
 *         <DialogDescription>...</DialogDescription>
 *       </DialogHeader>
 *       {body}
 *       <DialogFooter>...</DialogFooter>
 *     </DialogContent>
 *   </Dialog>
 */
export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;
export const DialogClose = DialogPrimitive.Close;

export const DialogOverlay = forwardRef(function DialogOverlay({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        "fixed inset-0 z-[1000] bg-[rgba(3,8,22,0.72)] backdrop-blur-[4px]",
        "data-[state=open]:animate-fadeIn data-[state=closed]:opacity-0",
        className,
      )}
      {...props}
    />
  );
});

export const DialogContent = forwardRef(function DialogContent({ className, children, ...props }, ref) {
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed left-1/2 top-1/2 z-[1001] -translate-x-1/2 -translate-y-1/2",
          "w-[92vw] max-w-[560px] max-h-[88vh] overflow-auto",
          "bg-rc-surface border border-rc-border rounded-[14px] text-rc-text-primary",
          "shadow-rc-lg p-6",
          "data-[state=open]:animate-fadeSlide",
          className,
        )}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});

export function DialogHeader({ className, children }) {
  return (
    <div className={cn("mb-4 flex flex-col gap-1", className)}>
      {children}
    </div>
  );
}

export function DialogFooter({ className, children }) {
  return (
    <div className={cn("mt-6 flex justify-end gap-2.5", className)}>
      {children}
    </div>
  );
}

export const DialogTitle = forwardRef(function DialogTitle({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn("text-[17px] font-extrabold text-rc-text-primary", className)}
      {...props}
    />
  );
});

export const DialogDescription = forwardRef(function DialogDescription({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cn("text-[13px] text-rc-text-secondary leading-[1.55]", className)}
      {...props}
    />
  );
});
